import { createContext, useContext, useEffect } from 'react';

type ModalStackContextType = {
    registerModal: (modalId: string, onClose?: () => void) => void;
    unregisterModal: (modalId: string) => void;
    isModalOpen: (modalId: string) => boolean;
};

const ModalStackContext = createContext<ModalStackContextType | undefined>(
    undefined
);

export const ModalStackProvider = ({
    children,
}: {
    children: React.ReactNode;
}) => {
    const stack = new Map<string, () => void>();

    const registerModal = (
        modalId: string,
        onClose = () => {
            /* do nothing */
        }
    ) => {
        stack.set(modalId, onClose);
    };

    const unregisterModal = (modalId: string) => {
        stack.get(modalId)?.(); // call the close handler
        stack.delete(modalId);
    };

    const closeTopModal = () => {
        const topModalId = [...stack.keys()].pop();
        if (topModalId) {
            unregisterModal(topModalId);
        }
    };

    const isModalOpen = (modalId: string) => stack.has(modalId);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                closeTopModal();
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    return (
        <ModalStackContext.Provider
            value={{
                registerModal,
                unregisterModal,
                isModalOpen,
            }}
        >
            {children}
        </ModalStackContext.Provider>
    );
};

export const useModalStack = () => {
    const context = useContext(ModalStackContext);

    if (!context) {
        throw new Error(
            'useModalStack must be used within a ModalStackProvider'
        );
    }
    return context;
};
